import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import GC, { dateTimeFormat } from '../../../../util/GlobalConstant';

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import style from './index.scss';
import { flattenChartData } from './chartsUtil';

/**
 * NoDataPanel
 * @param onTogglePanelVisibility
 * @param onReload
 * @returns {*}
 * @constructor
 */
const NoDataPanel = ({ onTogglePanelVisibility, onReload }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Alerts Yield</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>No Alerts Yield data exists for the period selected.</p>
            <p>
              <a className={style.retryLink} onClick={onReload}>
                Click here to fetch alerts yield data again.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * IsLoadingPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const IsLoadingPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Alerts Yield</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>Loading Alerts Yield chart data...</p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * MinimizedPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const MinimizedPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Alerts Yield</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-maximize"></i>
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * AlertsYieldChart
 * @param props
 * @returns {*}
 * @constructor
 */
export const AlertsYieldChart = (props) => {
  const {
    data,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload
  } = props;

  if (isCollapsed) {
    return <MinimizedPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (isLoading) {
    return <IsLoadingPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (Array.isArray(data) && data.length > 0) {
    let formattedData = [];
    data.forEach((item) => {
      const formattedItem = {
        ...item,
        date: moment
          .utc(item.date, [dateTimeFormat])
          .local()
          .format('DD/MM/YYYY')
      };

      formattedData.push(formattedItem);
    });

    const flattenedData = flattenChartData(formattedData, [
      'tookitaki',
      'ruleBased'
    ]);

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>Alerts Yield</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              {/* Figure Area Chart goes here */}
              <ResponsiveContainer
                width="100%"
                height={260}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                <LineChart data={flattenedData}>
                  <defs>
                    <linearGradient
                      id="colorCurrentSystem"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.anomalyColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.anomalyColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorTookitaki"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.ruleBasedColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.ruleBasedColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    height={60}
                    tickCount={5}
                    dataKey="date"
                    tickFormatter={dateTickFormatter}
                    tick={<CustomizedAxisTick />}
                  />
                  <YAxis type="number" unit="%" />

                  <CartesianGrid strokeDasharray="5 5" vertical={false} />
                  <Tooltip content={<CustomTooltip data={flattenedData} />} />
                  <Legend formatter={legendFormatter} />
                  <Line
                    type="monotone"
                    dataKey="tookitaki"
                    stroke={GC.anomalyColor}
                  />
                  <Line
                    type="monotone"
                    dataKey="ruleBased"
                    stroke={GC.ruleBasedColor}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataPanel
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
    />
  );
};
/**
 * dateTickFormatter
 * @param tickitem
 * @returns {string}
 */
const dateTickFormatter = (tickitem) => {
  const tickDate = moment(tickitem, [dateTimeFormat]);
  if (tickDate.isValid()) {
    return moment(tickitem, [dateTimeFormat]).format('DD/MM/YYYY');
  }

  return '';
};
/**
 * CustomTooltip
 * @param props
 * @returns {null|*}
 * @constructor
 */
const CustomTooltip = (props) => {
  const { active, payload, text, label, data } = props;

  if (!data || !payload) {
    return null;
  }

  let pointLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'tookitaki') {
      itemNameLabel = 'Tookitaki';
    }

    if (item.name === 'ruleBased') {
      itemNameLabel = 'Current System';
    }
    pointLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} :{' '}
        <span style={{ float: 'right' }}>
          {!isNaN(item.value) ? Number(item.value).toFixed(2) : item.value}%
        </span>
      </li>
    );
  });

  let accumulatedLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'tookitaki') {
      itemNameLabel = 'Tookitaki';
    }

    if (item.name === 'ruleBased') {
      itemNameLabel = 'Current System';
    }
    accumulatedLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} :{' '}
        <span style={{ float: 'right' }}>
          {accumulater(data, label, item.name)}
        </span>
      </li>
    );
  });

  let startingDate = '';
  let selectedDate = '';

  const startingDateValue = moment(data[0].date, ['DD/MM/YYYY']);
  if (startingDateValue.isValid()) {
    startingDate = data[0].date;
  }

  const selectedDateValue = moment(label, ['DD/MM/YYYY']);
  if (selectedDateValue.isValid()) {
    selectedDate = label;
  }

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {selectedDate}
          </p>
          <ul className="list-unstyled">{pointLists}</ul>
        </div>
      </div>
    </div>
  ) : null;
};
/**
 * accumulater
 * @param data
 * @param label
 * @param name
 * @returns {any}
 */
const accumulater = (data, label, name) => {
  let result = 0;

  let i = 0;
  let isLooping = true;
  while (isLooping) {
    const item = data[i];
    result += item[name];

    i++;

    if (item.date === label) {
      isLooping = false;
    }
  }

  return !isNaN(result) ? Number(result).toFixed(2) : result;
};
/**
 * legendFormatter
 * @param value
 * @param entry
 * @param index
 * @returns {string}
 */
const legendFormatter = (value, entry, index) => {
  if (value) {
    if (value === 'tookitaki') {
      return 'Tookitaki';
    }

    if (value === 'ruleBased') {
      return 'Current System';
    }
  }

  return '';
};
/**
 * CustomizedAxisTick
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomizedAxisTick = (props) => {
  const { x, y, stroke, payload, visibleTicksCount, index } = props;
  const tickValue = moment(payload.value);

  if (tickValue.isValid()) {
    const formattedTickDate = tickValue.local().format('DD/MM/YYYY');
    if (index === visibleTicksCount - 1) {
      return (
        <g transform={`translate(${x + 20},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    } else {
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    }
  }

  if (index === visibleTicksCount - 1) {
    return (
      <g transform={`translate(${x + 20},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  } else {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  }
};

AlertsYieldChart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func
};
